/**
 * S-3021: useCloudflareUpload Hook
 * 
 * Handles direct upload to Cloudflare Stream via signed upload URLs.
 * 
 * Flow:
 * 1. Call edge function to get signed uploadURL and uid
 * 2. Upload file directly to Cloudflare via PUT
 * 3. Insert swing_runs record with processing status
 * 4. Return uid and runId for optimistic UI update
 */

import { useCallback, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

// ============================================================================
// Types
// ============================================================================

export type CloudflareUploadState = 
  | { phase: 'idle' }
  | { phase: 'validating' }
  | { phase: 'getting_url' }
  | { phase: 'uploading'; progress: number }
  | { phase: 'saving_record' }
  | { phase: 'done'; uid: string; runId: string }
  | { phase: 'error'; message: string };

interface CloudflareUploadOptions {
  onProgress?: (progress: number) => void;
  onComplete?: (uid: string, runId: string) => void;
  onError?: (error: Error) => void;
}

interface EdgeFunctionResponse {
  ok: boolean;
  uploadURL?: string;
  uid?: string;
  user_id?: string;
  error?: string;
}

// ============================================================================
// Hook
// ============================================================================

export function useCloudflareUpload(options?: CloudflareUploadOptions) {
  const [state, setState] = useState<CloudflareUploadState>({ phase: 'idle' });
  const abortControllerRef = useRef<AbortController | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);

  /**
   * Validate file before upload
   */
  const validateFile = useCallback(async (file: File) => {
    console.log('[S-3021] Validating file:', {
      filename: file.name,
      size: file.size,
      type: file.type,
      timestamp: new Date().toISOString(),
    });

    // Type check
    const acceptedTypes = ['video/mp4', 'video/quicktime', 'video/mov'];
    if (!acceptedTypes.includes(file.type.toLowerCase())) {
      throw new Error('Please upload MP4 or MOV format.');
    }

    // Size check: Allow up to 1GB (generous for Cloudflare)
    const maxSize = 1024 * 1024 * 1024; // 1GB
    if (file.size > maxSize) {
      throw new Error('File must be ≤ 1GB.');
    }

    // Basic duration check via metadata
    const url = URL.createObjectURL(file);
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = url;
      
      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject(new Error('Could not read video metadata.'));
        // Timeout after 5 seconds
        setTimeout(() => reject(new Error('Metadata loading timeout.')), 5000);
      });

      console.log('[S-3021] Video metadata:', {
        duration: video.duration,
        videoWidth: (video as any).videoWidth,
        videoHeight: (video as any).videoHeight,
      });

      // Duration check (optional - Cloudflare can handle long videos)
      // Leaving this flexible for now, can add limits later
      if (video.duration > 600) { // 10 minutes
        console.warn('[S-3021] Long video detected:', video.duration, 'seconds');
      }
    } finally {
      URL.revokeObjectURL(url);
    }
  }, []);

  /**
   * Get signed upload URL from edge function
   */
  const getUploadURL = useCallback(async (): Promise<{ uploadURL: string; uid: string; user_id: string }> => {
    console.log('[S-3021] Getting upload URL from edge function');

    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      console.error('[S-3021] Session error:', sessionError);
      throw new Error('You must be logged in to upload videos.');
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('Application not configured properly. Missing VITE_SUPABASE_URL.');
    }

    const startTime = Date.now();

    // Call edge function
    const response = await fetch(
      `${supabaseUrl}/functions/v1/cloudflare-direct-upload`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[S-3021] Edge function error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
        duration_ms: duration,
      });

      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (response.status === 500) {
        throw new Error('Upload service temporarily unavailable. Please try again.');
      } else {
        throw new Error(`Failed to get upload URL: ${response.statusText}`);
      }
    }

    const data: EdgeFunctionResponse = await response.json();

    console.log('[S-3021] Got upload URL:', {
      ok: data.ok,
      has_uploadURL: !!data.uploadURL,
      uid: data.uid,
      user_id: data.user_id,
      duration_ms: duration,
    });

    if (!data.ok || !data.uploadURL || !data.uid || !data.user_id) {
      console.error('[S-3021] Invalid edge function response:', data);
      throw new Error('Invalid response from upload service.');
    }

    return {
      uploadURL: data.uploadURL,
      uid: data.uid,
      user_id: data.user_id,
    };
  }, []);

  /**
   * Upload file to Cloudflare Stream
   * Uses XMLHttpRequest for progress tracking (better browser support than fetch)
   */
  const uploadToCloudflare = useCallback(async (
    uploadURL: string,
    uid: string,
    file: File
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      const startTime = Date.now();

      console.log('[S-3021] Starting Cloudflare upload:', {
        uid,
        filename: file.name,
        size: file.size,
        user_agent: navigator.userAgent,
      });

      // Progress tracking
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          
          setState({ phase: 'uploading', progress });
          options?.onProgress?.(progress);

          // Log progress at 25%, 50%, 75%, 100%
          if (progress % 25 === 0) {
            console.log('[S-3021] Upload progress:', {
              uid,
              progress,
              loaded: event.loaded,
              total: event.total,
              elapsed_ms: Date.now() - startTime,
            });
          }
        }
      });

      // Upload complete
      xhr.upload.addEventListener('load', () => {
        console.log('[S-3021] Upload stream complete:', { uid });
      });

      // Request complete
      xhr.addEventListener('load', () => {
        const duration = Date.now() - startTime;
        const speedMbps = (file.size / (1024 * 1024)) / (duration / 1000);

        console.log('[S-3021] Upload complete:', {
          uid,
          status: xhr.status,
          duration_ms: duration,
          size_mb: file.size / (1024 * 1024),
          speed_mbps: speedMbps,
        });

        if (xhr.status >= 200 && xhr.status < 300) {
          xhrRef.current = null;
          resolve();
        } else {
          console.error('[S-3021] Cloudflare upload failed:', {
            uid,
            status: xhr.status,
            statusText: xhr.statusText,
            response: xhr.responseText,
          });
          xhrRef.current = null;
          reject(new Error(`Upload to Cloudflare failed: ${xhr.status} ${xhr.statusText}`));
        }
      });

      // Error handling
      xhr.addEventListener('error', () => {
        console.error('[S-3021] Upload network error:', {
          uid,
          user_agent: navigator.userAgent,
          duration_ms: Date.now() - startTime,
        });
        xhrRef.current = null;
        reject(new Error('Network error during upload. Please check your connection.'));
      });

      xhr.addEventListener('abort', () => {
        console.log('[S-3021] Upload cancelled by user:', { uid });
        xhrRef.current = null;
        reject(new Error('Upload cancelled'));
      });

      // Open and send request
      xhr.open('PUT', uploadURL);
      xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
      xhr.send(file);
    });
  }, [options]);

  /**
   * Insert swing_runs record in database
   */
  const insertSwingRun = useCallback(async (
    uid: string,
    user_id: string,
    filename: string
  ): Promise<string> => {
    console.log('[S-3021] Inserting swing_runs record:', {
      uid,
      user_id,
      filename,
    });

    const { data, error } = await supabase
      .from('swing_runs')
      .insert({
        user_id: user_id,
        status: 'processing',
        cf_stream_uid: uid,
        cf_playback_url: `https://watch.cloudflarestream.com/${uid}`,
        original_filename: filename,
        schema_version: 'v1',
      })
      .select('id')
      .single();

    if (error) {
      console.error('[S-3021] CRITICAL: Failed to insert swing_runs:', {
        uid,
        error: error.message,
        code: error.code,
        details: error.details,
      });
      throw new Error('Upload succeeded but failed to save record. Please contact support with UID: ' + uid);
    }

    const runId = data.id;

    console.log('[S-3021] Database record created:', {
      uid,
      runId,
    });

    return runId;
  }, []);

  /**
   * Main upload function
   */
  const upload = useCallback(async (file: File): Promise<{ uid: string; runId: string }> => {
    const overallStartTime = Date.now();

    try {
      // Phase 1: Validate file
      setState({ phase: 'validating' });
      await validateFile(file);

      // Phase 2: Get upload URL
      setState({ phase: 'getting_url' });
      const { uploadURL, uid, user_id } = await getUploadURL();

      // Phase 3: Upload to Cloudflare
      setState({ phase: 'uploading', progress: 0 });
      await uploadToCloudflare(uploadURL, uid, file);

      // Phase 4: Save database record
      setState({ phase: 'saving_record' });
      const runId = await insertSwingRun(uid, user_id, file.name);

      // Phase 5: Complete
      setState({ phase: 'done', uid, runId });

      const totalDuration = Date.now() - overallStartTime;
      console.log('[S-3021] Upload flow complete:', {
        uid,
        runId,
        total_duration_ms: totalDuration,
      });

      options?.onComplete?.(uid, runId);

      return { uid, runId };

    } catch (error: any) {
      const errorMessage = error?.message || 'Upload failed';
      
      console.error('[S-3021] Upload error:', {
        error: errorMessage,
        name: error?.name,
        user_agent: navigator.userAgent,
        filename: file.name,
        size: file.size,
        duration_ms: Date.now() - overallStartTime,
      });

      // User-friendly error messages
      let userMessage = errorMessage;

      if (error.name === 'AbortError' || errorMessage.includes('cancelled')) {
        userMessage = 'Upload cancelled';
      } else if (errorMessage.includes('Network error') || errorMessage.includes('Failed to fetch')) {
        userMessage = 'Network error. Please check your connection and try again.';
      } else if (errorMessage.includes('Authentication failed')) {
        userMessage = 'Session expired. Please log in again.';
      } else if (errorMessage.includes('contact support')) {
        // Critical error - upload succeeded but DB failed
        // Keep the detailed message with UID
        userMessage = errorMessage;
      }

      setState({ phase: 'error', message: userMessage });
      
      const errorObj = new Error(userMessage);
      options?.onError?.(errorObj);

      throw errorObj;
    } finally {
      // Clean up
      abortControllerRef.current = null;
      xhrRef.current = null;
    }
  }, [validateFile, getUploadURL, uploadToCloudflare, insertSwingRun, options]);

  /**
   * Cancel upload
   */
  const cancel = useCallback(() => {
    console.log('[S-3021] Cancelling upload');
    
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setState({ phase: 'idle' });
  }, []);

  return {
    state,
    upload,
    cancel,
  };
}

